'use client'

import React, { useState, useMemo, useEffect } from 'react'
import { useVendorInfo, useVendorProducts, useVendorCourses, useVendorSubcategories } from '@/hooks/useMenuWebsiteApi'
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { CheckCircle } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { Card, CardContent } from "@/components/ui/card"
import { toast } from "sonner"
import { useAnalytics } from '@/hooks/useAnalytics'
// import { CustomerDetailsCard } from './CustomerDetailsCard'
import { OrderCart } from './OrderCart'
import { ProductSearch } from './ProductSearch'
// import { ProductSearchFilter } from './ProductSearchFilter'
import { ComboSelectionSheet } from './ComboSelectionSheet'
import { useCustomerDetailsManagement } from '../hooks/useCustomerDetailsManagement'
import { CartItem, DisplayProduct, ComboSelection } from '../types'
import { clearOrderData } from '../utils/localStorage'

// Update interface to include linkCode
export interface CustomerOrderFormProps {
  vendorId: string;
  linkCode: string;
}

export function CustomerOrderForm({ vendorId, linkCode }: CustomerOrderFormProps) {
  const [selectedProducts, setSelectedProducts] = useState<Record<string, number>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [orderSuccess, setOrderSuccess] = useState(false)
  const [isInIframe, setIsInIframe] = useState(false)
  const [selectedCombo, setSelectedCombo] = useState<DisplayProduct | null>(null)
  const [isComboSheetOpen, setIsComboSheetOpen] = useState(false)
  const { trackOrderCreated } = useAnalytics()
  
  // API hooks
  const { data: vendorInfoResponse, isLoading: vendorLoading } = useVendorInfo(vendorId)
  const { data: productsResponse, isLoading: productsLoading } = useVendorProducts(vendorId)
  const { data: coursesResponse, isLoading: coursesLoading } = useVendorCourses(vendorId)
  const { data: subcategoriesResponse, isLoading: subcategoriesLoading } = useVendorSubcategories(vendorId)
  
  const courses = useMemo(() => coursesResponse?.data?.courses || [], [coursesResponse?.data?.courses])
  const subcategories = useMemo(() => subcategoriesResponse?.data?.subcategories || [], [subcategoriesResponse?.data?.subcategories])
  
  const vendorInfo = vendorInfoResponse?.data
  const rawProducts = useMemo(() => productsResponse?.data?.products || [], [productsResponse?.data?.products])
  const rawCombos = useMemo(() => productsResponse?.data?.combos || [], [productsResponse?.data?.combos])
  const isLoading = vendorLoading || productsLoading || coursesLoading || subcategoriesLoading

  // Create course ID to name mapping
  const courseIdToNameMap = useMemo(() => {
    const map = new Map<string, string>();
    courses.forEach(course => {
      map.set(course.id, course.name);
    });
    return map;
  }, [courses]);

  // Enhance products with course names and convert combos to DisplayProduct format
  const products = useMemo(() => {
    // Convert regular products
    const productItems: DisplayProduct[] = rawProducts.map(product => ({
      ...product,
      course_name: product.food_course ? courseIdToNameMap.get(product.food_course) || 'Unknown Course' : 'Other',
      subcategory_id: product.subcategory_id || null,
      type: 'product' as const
    }));

    // Convert combos to DisplayProduct format with proper typing
    const comboItems: DisplayProduct[] = rawCombos.map((combo) => {
      const typedCombo = combo as {
        id: string;
        name: string;
        description?: string;
        image_url?: string;
        course_id?: string;
        combo_groups?: Array<{
          id: string;
          name: string;
          is_optional?: boolean;
          display_order?: number;
          group_type: 'selection' | 'individual';
          combo_group_products?: Array<{
            id: string;
            product_id: string;
            vendor_products?: {
              id: string;
              name: string;
              description?: string;
              image_url?: string;
              is_vegetarian: boolean;
              contains_egg: boolean;
            };
          }>;
        }>;
      };

      return {
        id: typedCombo.id,
        name: typedCombo.name,
        description: typedCombo.description || null,
        image_url: typedCombo.image_url || null,
        food_course: typedCombo.course_id || null,
        subcategory_id: null, // Combos don't have subcategories yet
        course_name: typedCombo.course_id ? courseIdToNameMap.get(typedCombo.course_id) || 'Unknown Course' : 'Other',
        is_vegetarian: false, // TODO: Calculate based on combo groups
        contains_egg: false, // TODO: Calculate based on combo groups
        not_a_food: false,
        type: 'combo' as const,
        combo_groups: typedCombo.combo_groups?.map(group => ({
          id: group.id,
          name: group.name,
          is_optional: group.is_optional || null,
          display_order: group.display_order || null,
          group_type: group.group_type,
          combo_group_products: group.combo_group_products?.map(cgp => ({
            id: cgp.id,
            product_id: cgp.product_id,
            vendor_products: cgp.vendor_products ? {
              id: cgp.vendor_products.id,
              name: cgp.vendor_products.name,
              description: cgp.vendor_products.description || null,
              image_url: cgp.vendor_products.image_url || null,
              is_vegetarian: cgp.vendor_products.is_vegetarian,
              contains_egg: cgp.vendor_products.contains_egg
            } : undefined
          }))
        }))
      };
    });

    // Combine and sort by name
    return [...productItems, ...comboItems].sort((a, b) => a.name.localeCompare(b.name));
  }, [rawProducts, rawCombos, courseIdToNameMap]);
  
  // Use the customer details management hook
  const {
    customerDetails,
    handleEditCustomerDetails,
    handleNavigateToCustomerDetails
  } = useCustomerDetailsManagement();

  // Create a Set of selected product IDs for ProductSearch
  const selectedProductsSet = useMemo(() => {
    return new Set(Object.keys(selectedProducts));
  }, [selectedProducts]);

  // Store combo selections separately
  const [comboSelections, setComboSelections] = useState<Record<string, ComboSelection[]>>({});

  // Convert selectedProducts to a cart items array for the OrderCart component
  const cartItems = useMemo(() => {
    return Object.entries(selectedProducts).map(([productId, quantity]) => {
      const product = products.find(p => p.id === productId);
      if (!product) return null;
      
      return {
        id: productId,
        product_id: productId,
        product_name: product.name,
        quantity: quantity,
        price: 0, // We're not using price
        image_url: product.image_url || null,
        food_course: product.food_course || null,
        course_name: product.course_name, // Add course name for grouping
        is_vegetarian: product.is_vegetarian,
        contains_egg: product.contains_egg,
        type: product.type,
        combo_id: product.type === 'combo' ? productId : undefined,
        combo_selections: product.type === 'combo' ? comboSelections[productId] : undefined,
        description: product.description // Include the product description
      } as CartItem & { description?: string | null; course_name?: string };
    }).filter(Boolean) as (CartItem & { description?: string | null })[];
  }, [selectedProducts, products, comboSelections]);

  // Check if we're in an iframe
  useEffect(() => {
    setIsInIframe(window.self !== window.top);
  }, []);

  // Load cart state from localStorage when component mounts (user returning from cart or customer details page)
  useEffect(() => {
    // First try to restore from CART_DATA (returning from cart page)
    const storedCartData = localStorage.getItem('cartData');
    if (storedCartData) {
      try {
        const parsedCartData = JSON.parse(storedCartData);
        if (parsedCartData.items && Array.isArray(parsedCartData.items)) {
          // Convert cart items back to selectedProducts state
          const restoredSelectedProducts: Record<string, number> = {};
          const restoredComboSelections: Record<string, ComboSelection[]> = {};
          
          parsedCartData.items.forEach((item: CartItem & { course_name?: string }) => {
            restoredSelectedProducts[item.product_id] = item.quantity;
            if (item.type === 'combo' && item.combo_selections) {
              restoredComboSelections[item.product_id] = item.combo_selections;
            }
          });
          
          setSelectedProducts(restoredSelectedProducts);
          setComboSelections(restoredComboSelections);
          
          console.log('🔄 Restored cart state from cartData:', {
            selectedProducts: restoredSelectedProducts,
            comboSelections: restoredComboSelections
          });
        }
        // Clear cart data after restoring
        localStorage.removeItem('cartData');
      } catch (error) {
        console.error('Error parsing stored cart data:', error);
      }
    } else {
      // Fallback: try to restore from selectedProducts (returning from customer details page)
      const storedSelectedProducts = localStorage.getItem('selectedProducts');
      if (storedSelectedProducts) {
        try {
          const parsedSelectedProducts = JSON.parse(storedSelectedProducts);
          setSelectedProducts(parsedSelectedProducts);
          // Clear from localStorage after loading to avoid conflicts
          localStorage.removeItem('selectedProducts');
          
          console.log('🔄 Restored selected products from selectedProducts:', parsedSelectedProducts);
        } catch (error) {
          console.error('Error parsing stored selected products:', error);
        }
      }
    }
  }, []);

  // Add page unload cleanup only (no beforeunload warning to avoid window switching issues)
  useEffect(() => {
    const handleUnload = () => {
      // Clear localStorage when page is actually unloaded (refresh/close)
      clearOrderData();
    };

    // Only listen to unload event for cleanup, not beforeunload to avoid window switching issues
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('unload', handleUnload);
    };
  }, []);

  // Helper function to check if combo is simple (all groups have exactly 1 product and no optional products)
  const isSimpleCombo = (combo: DisplayProduct): boolean => {
    console.log('🔍 COMBO DEBUG - Checking if combo is simple:', combo.name);
    console.log('🔍 COMBO DEBUG - Combo groups:', JSON.stringify(combo.combo_groups, null, 2));
    
    if (!combo.combo_groups || combo.combo_groups.length === 0) {
      console.log('🔍 COMBO DEBUG - No combo groups found, not simple');
      return false;
    }
    
    const isSimple = combo.combo_groups.every(group => {
      // Check if group is not optional and has exactly 1 product
      const hasOneProduct = group.combo_group_products && group.combo_group_products.length === 1;
      const isNotOptional = !group.is_optional;
      
      console.log(`🔍 COMBO DEBUG - Group "${group.name}": optional=${group.is_optional}, products=${group.combo_group_products?.length}, qualifies=${isNotOptional && hasOneProduct}`);
      
      return isNotOptional && hasOneProduct;
    });
    
    console.log('🔍 COMBO DEBUG - Final simple combo result:', isSimple);
    return isSimple;
  };

  // Helper function to auto-generate combo selections for simple combos
  const generateSimpleComboSelections = (combo: DisplayProduct): ComboSelection[] => {
    if (!combo.combo_groups) return [];
    
    const selections: ComboSelection[] = [];
    
    combo.combo_groups.forEach(group => {
      if (group.combo_group_products && group.combo_group_products.length === 1) {
        const groupProduct = group.combo_group_products[0];
        if (groupProduct.vendor_products) {
          selections.push({
            group_id: group.id,
            group_name: group.name,
            selected_product_id: groupProduct.product_id,
            selected_product_name: groupProduct.vendor_products.name,
            selected_product_image: groupProduct.vendor_products.image_url,
            is_vegetarian: groupProduct.vendor_products.is_vegetarian,
            contains_egg: groupProduct.vendor_products.contains_egg
          });
        }
      }
    });
    
    return selections;
  };

  // Handler for adding products to cart
  const handleAddToCart = (product: DisplayProduct) => {
    if (product.type === 'combo') {
      // Check if combo is simple (all groups have exactly 1 product and no optional products)
      if (isSimpleCombo(product)) {
        // For simple combos, add directly to cart without opening selection sheet
        const selections = generateSimpleComboSelections(product);
        
        console.log('🔍 COMBO DEBUG - Simple combo detected:', product.name);
        console.log('🔍 COMBO DEBUG - Generated selections:', JSON.stringify(selections, null, 2));
        
        // Add combo to selected products
        setSelectedProducts(prev => ({
          ...prev,
          [product.id]: 1
        }));
        
        // Store combo selections
        setComboSelections(prev => ({
          ...prev,
          [product.id]: selections
        }));
        
        console.log('🔍 COMBO DEBUG - Combo added to cart with ID:', product.id);
      } else {
        // For complex combos, open the selection sheet
        setSelectedCombo(product);
        setIsComboSheetOpen(true);
      }
    } else {
      // For regular products, add/remove directly
      setSelectedProducts(prev => {
        const updated = { ...prev };
        
        // If product is already in cart, remove it
        if (updated[product.id]) {
          delete updated[product.id];
        } else {
          // Otherwise add it with quantity 1
          updated[product.id] = 1;
        }
        
        return updated;
      });
    }
  };

  // Handler for combo selection completion
  const handleComboSelected = (combo: DisplayProduct, selections: ComboSelection[]) => {
    console.log('🔍 COMBO DEBUG - Complex combo selected:', combo.name);
    console.log('🔍 COMBO DEBUG - Combo selections received:', JSON.stringify(selections, null, 2));
    
    // Add combo to selected products
    setSelectedProducts(prev => ({
      ...prev,
      [combo.id]: 1
    }));
    
    // Store combo selections
    setComboSelections(prev => ({
      ...prev,
      [combo.id]: selections
    }));
    
    console.log('🔍 COMBO DEBUG - Complex combo added to cart with ID:', combo.id);
  };

  // Handler for removing an item from cart
  const handleRemoveFromCart = (itemId: string) => {
    setSelectedProducts(prev => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
    
    // Also remove combo selections if it's a combo
    setComboSelections(prev => {
      const updated = { ...prev };
      delete updated[itemId];
      return updated;
    });
  };

  const handleSubmitOrder = async () => {
    // Check if any products are selected
    if (Object.keys(selectedProducts).length === 0) {
      toast.error("No products selected. Please select at least one product to place an order.")
      return Promise.reject(new Error('No products selected'));
    }

    // Check if customer details are provided
    if (!customerDetails) {
      // Pass cart data to customer details page
      const cartData = {
        items: cartItems,
        vendorId,
        linkCode,
        vendorInfo,
        courseIdToNameMap: Object.fromEntries(courseIdToNameMap)
      };
      handleNavigateToCustomerDetails(cartData, selectedProducts);
      return Promise.reject(new Error('NAVIGATE_TO_CUSTOMER_DETAILS')); // Special error for navigation
    }

    // Validate required customer details
    if (!customerDetails.name || !customerDetails.eventDate) {
      toast.error("Please fill in all required customer details (Name and Event Date).");
      handleEditCustomerDetails();
      return Promise.reject(new Error('NAVIGATE_TO_EDIT_DETAILS')); // Special error for navigation
    }

    // Additional validation to ensure we have valid data
    if (!vendorId) {
      toast.error("System error: Vendor ID is missing. Please refresh the page and try again.");
      return Promise.reject(new Error('Missing vendor ID'));
    }

    if (!customerDetails.name.trim()) {
      toast.error("Please enter a valid customer name.");
      handleEditCustomerDetails();
      return Promise.reject(new Error('Invalid customer name'));
    }

    setIsSubmitting(true);
    try {
      // Prepare cart items for structured database storage
      const cart_items = cartItems.map(item => ({
        product_id: item.type === 'combo' ? null : item.product_id, // Set to null for combos to avoid FK constraint violation
        quantity: item.quantity,
        combo_id: item.type === 'combo' ? item.product_id : null,
        combo_selections: item.type === 'combo' ? item.combo_selections : null
      }));
      
      console.log('🔍 FRONTEND DEBUG - cartItems structure:', JSON.stringify(cartItems, null, 2));
      console.log('🔍 FRONTEND DEBUG - cart_items being sent to API:', JSON.stringify(cart_items, null, 2));
      console.log('🔍 FRONTEND DEBUG - comboSelections state:', JSON.stringify(comboSelections, null, 2));
      console.log('🔍 FRONTEND DEBUG - vendorId:', vendorId);
      console.log('🔍 FRONTEND DEBUG - customerDetails:', JSON.stringify(customerDetails, null, 2));
      
      

      // Only include customer email in notes (products are now stored in order_items table)
      const customerEmail = customerDetails.email || 'N/A';
      const notesText = `Email: ${customerEmail}`;
      
      // Create a unique order link by appending a timestamp
      const uniqueOrderLink = `${linkCode}_${Date.now()}`;

      // Prepare the payload
      const payload = {
        vendor_id: vendorId,
        customer_name: customerDetails.name.trim(),
        customer_phone: customerDetails.phone?.trim() || null,
        customer_email: customerDetails.email?.trim() || null,
        event_date: customerDetails.eventDate!.toISOString(),
        event_end_date: customerDetails.eventEndDate ? customerDetails.eventEndDate.toISOString() : null,
        event_type: customerDetails.eventType?.trim() || 'General Event',
        event_address: customerDetails.eventAddress?.trim() || null,
        head_count: customerDetails.headCount || 1,
        notes: notesText,
        order_link: uniqueOrderLink,
        total_amount: 0,
        cart_items: cart_items
      };

      console.log('🔍 FRONTEND DEBUG - Full payload being sent:', JSON.stringify(payload, null, 2));

      // Create order via public API route with proper security validation
      const response = await fetch('/api/customer-orders-public', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Order creation failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.error || `Failed to create order (${response.status})`);
      }

      // Skip the insertion of order items since we're using vendor_products but
      // the schema expects links to the products table
      const orderData = await response.json();
      
      // Track customer order creation
      trackOrderCreated({
        orderId: orderData.id,
        itemCount: cartItems.reduce((sum, item) => sum + item.quantity, 0),
        orderType: 'customer_order',
      });

      toast.success("Your order has been successfully submitted!");

      // Reset form and selected products
      setSelectedProducts({});
      
      // Show success UI in the current page instead of redirecting
      // This avoids the link validation issue on the order-success page
      setOrderSuccess(true);
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Return a resolved promise for the OrderCart component
      return Promise.resolve();
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error("Failed to submit your order. Please try again.");
      return Promise.reject(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-0 sm:mx-auto py-2 sm:py-8 px-0 sm:px-4 relative">
        <div className="flex flex-col gap-8">
          {/* Search bar skeleton */}
          <div className="space-y-2">
            <div className="sticky top-0 z-10 bg-muted pt-4 pb-2 space-y-4 px-2 border-b">
              <Skeleton className="h-12 w-full rounded-xl" />
            </div>
          </div>

          {/* Course sections skeleton */}
          <div className="space-y-4 bg-white px-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="space-y-2">
                <div className="p-4">
                  {/* Course header skeleton */}
                  <div className="flex items-center justify-between mb-4">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-5 w-12" />
                  </div>
                  
                  {/* Product items skeleton */}
                  <div className="space-y-4">
                    {Array.from({ length: 2 }).map((_, j) => (
                      <div key={j} className="flex gap-4 p-4 border-b last:border-0">
                        <Skeleton className="w-24 h-24 rounded-lg flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex gap-1.5 mb-2">
                            <Skeleton className="h-4 w-4 rounded-full" />
                            <Skeleton className="h-4 w-4 rounded-full" />
                          </div>
                          <Skeleton className="h-5 w-3/4 mb-2" />
                          <Skeleton className="h-4 w-full mb-1" />
                          <Skeleton className="h-4 w-2/3 mb-3" />
                          <Skeleton className="h-8 w-20" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Show success UI if order was submitted successfully
  if (orderSuccess) {
    return (
      <div className="container mx-auto px-4 py-16 flex flex-col items-center justify-center min-h-[70vh]">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          
          <h1 className="text-2xl font-bold mb-2">Order Submitted Successfully!</h1>
          
          <p className="text-gray-600 mb-6">
            Thank you for your order. {vendorInfo && typeof vendorInfo === 'object' && vendorInfo.company_name ? 
              `${String(vendorInfo.company_name)} will` : 'The vendor will'} review your request and get back to you soon.
          </p>
          
          {!isInIframe && (
            <div className="space-y-3">
              <Button 
                onClick={() => window.location.reload()} 
                className="w-full"
              >
                Place Another Order
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-0 sm:mx-auto py-2 sm:py-8 px-0 sm:px-4 relative">
      <div className="flex flex-col gap-8">

      {/* {cartItems.length > 0 && customerDetails && (
              <CustomerDetailsCard 
                customerDetails={customerDetails} 
                onEdit={handleEditCustomerDetails} 
              />
            )} */}
        {/* Menu Section */}
        <div>
          {products.length === 0 ? (
            <Alert className="bg-yellow-50 border-yellow-100">
              <AlertDescription>
                No menu items available at the moment.
              </AlertDescription>
            </Alert>
          ) : (
            <ProductSearch
              products={products}
              onAddToCart={handleAddToCart}
              selectedProducts={selectedProductsSet}
              courses={courses}
              subcategories={subcategories}
            />
          )}
        </div>

        {/* Cart & Customer Details Section */}
        <div className="space-y-6">
          {/* Order Cart */}
          <OrderCart
            cart={cartItems}
            linkCode={linkCode}
          />

        </div>
      </div>
      
      {/* Menu Filter Button */}
      {/* {products.length > 0 && (
        <ProductSearchFilter 
          courses={products.reduce((acc, product) => {
            if (product.food_course) {
              const courseName = courseIdToNameMap.get(product.food_course) || 'Unknown Course';
              const existingCourse = acc.find(c => c.course === courseName);
              if (existingCourse) {
                existingCourse.count++;
              } else {
                acc.push({ course: courseName, count: 1 });
              }
            }
            return acc;
          }, [] as { course: string; count: number }[])}
          totalItems={products.length}
        />
      )} */}

      {/* Combo Selection Sheet */}
      <ComboSelectionSheet
        isOpen={isComboSheetOpen}
        onOpenChange={setIsComboSheetOpen}
        combo={selectedCombo}
        onComboSelected={handleComboSelected}
      />
    </div>
  );
}